import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './TopicCatalog.css';

export default function TopicCatalog({ apiUrl, subjects, courseSubject, onChanged }) {
  const csId = courseSubject?.course_subject_id;
  const subjectId = courseSubject?.id;
  const [catalog, setCatalog] = useState([]);
  const [linked, setLinked] = useState([]);
  const [source, setSource] = useState('');
  const [createSource, setCreateSource] = useState(subjectId ? String(subjectId) : '');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [name, setName] = useState('');
  const [archived, setArchived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [revision, setRevision] = useState(0);
  const [usages, setUsages] = useState(null);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${apiUrl}adm/catalogTopics`, { params: { includeArchived: !csId && archived } }),
      csId ? axios.get(`${apiUrl}adm/topics/${csId}`) : Promise.resolve({ data: { topics: [] } })
    ]).then(([content, edital]) => {
      if (!cancelled) { setCatalog(content.data.topics); setLinked(edital.data.topics); }
    }).catch(() => { if (!cancelled) setError('Não foi possível carregar os tópicos. Clique em Atualizar catálogo para tentar novamente.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apiUrl, csId, archived, revision]);

  useEffect(() => {
    setSelected([]); setUsages(null); setName(''); setEditing(null);
    setCreateSource(subjectId ? String(subjectId) : '');
  }, [csId, subjectId]);

  const act = async (work, message) => {
    setBusy(true); setError(''); setNotice('');
    try {
      await work(); setRevision(v => v + 1); setNotice(message || 'Alteração salva.');
      if (onChanged) onChanged();
    } catch (e) { setError(e.response?.data?.message || 'Não foi possível salvar. Tente novamente.'); }
    finally { setBusy(false); }
  };
  const filtered = catalog.filter(t => (!source || String(t.subject_id) === source) &&
    t.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()) &&
    (!csId || !linked.some(l => Number(l.catalog_topic_id) === Number(t.id))));
  const rootSubjects = subjects.filter(s => !s.canonical_subject_id);
  const createTopic = event => {
    event.preventDefault();
    if (!createSource) { setError('Selecione a matéria do novo tópico antes de criar.'); return; }
    if (!name.trim()) return;
    act(async () => {
      const { data } = await axios.post(`${apiUrl}adm/catalogTopics`, { subject_id: Number(createSource), name: name.trim() });
      setName('');
      // O conteúdo já estará no catálogo mesmo se o vínculo falhar.
      setRevision(v => v + 1);
      if (csId) await axios.post(`${apiUrl}adm/courseSubjects/${csId}/topics`, { catalog_topic_ids: [data.id] });
    }, csId ? 'Tópico criado e adicionado ao edital.' : 'Tópico criado no catálogo.');
  };
  const move = (index, direction) => act(async () => {
    const reordered = [...linked];
    [reordered[index], reordered[index + direction]] = [reordered[index + direction], reordered[index]];
    await axios.put(`${apiUrl}adm/courseSubjects/${csId}/topicOrder`, { topic_ids: reordered.map(t => t.id) });
  }, 'Ordem dos tópicos atualizada.');
  const editName = (t, local) => setEditing({ id: t.id, name: t.name, local });
  const renderEditor = t => <form className="tc-edit-form" onSubmit={event => {
    event.preventDefault();
    if (editing.name.trim()) act(async () => {
      const url = editing.local ? `adm/courseSubjects/${csId}/topics/${t.id}` : `adm/catalogTopics/${t.id}`;
      await axios.put(`${apiUrl}${url}`, { name: editing.name.trim() }); setEditing(null);
    }, 'Nome atualizado.');
  }}>
    <input aria-label="Editar nome do tópico" autoFocus maxLength={255} value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} />
    <button className="tc-button tc-button-primary" type="submit" disabled={!editing.name.trim()}>Salvar nome</button>
    <button className="tc-button" type="button" onClick={() => setEditing(null)}>Cancelar</button>
  </form>;

  return <section className="topic-catalog admin-card" aria-label={csId ? 'Tópicos da matéria do concurso' : 'Catálogo de tópicos'}>
    <header className="tc-header">
      <div><span className="tc-eyebrow">{csId ? 'Conteúdo do edital' : 'Biblioteca de conteúdo'}</span>
        <h3>{csId ? `Tópicos de ${courseSubject.name}` : 'Catálogo de tópicos por matéria'}</h3>
        <p>{csId ? 'Crie um tópico nesta matéria ou reaproveite conteúdos de outras matérias.' : 'Organize seus tópicos por matéria e reutilize o conteúdo nos concursos.'}</p>
      </div>
      <button className="tc-button" type="button" disabled={busy || loading} onClick={() => { setError(''); setRevision(v => v + 1); }}>Atualizar catálogo</button>
    </header>
    {error && <p className="tc-feedback tc-feedback-error" role="alert">{error}</p>}
    {notice && !error && <p className="tc-feedback" role="status">{notice}</p>}
    <fieldset disabled={busy || loading}>
      {csId && <div className="tc-section">
        <div className="tc-section-heading"><h4>Tópicos nesta matéria</h4><span className="tc-count">{linked.length} adicionados</span></div>
        {linked.length ? <ul className="topic-catalog-list">
          {linked.map((t, index) => <li key={t.id}>
            {editing?.local && editing.id === t.id ? renderEditor(t) : <>
              <span className="tc-topic-info">
                <span className="tc-topic-title"><strong>{t.name}</strong><span className="tc-count">{Number(t.questions_count || 0)} {Number(t.questions_count) === 1 ? 'questão' : 'questões'}</span></span>
                <small>Origem: {t.source_subject_name || courseSubject.name}</small>
              </span>
              <div className="topic-catalog-actions">
                <button className="tc-button tc-button-icon" type="button" disabled={index === 0} onClick={() => move(index, -1)} aria-label={`Subir ${t.name}`} title="Mover para cima">↑</button>
                <button className="tc-button tc-button-icon" type="button" disabled={index === linked.length - 1} onClick={() => move(index, 1)} aria-label={`Descer ${t.name}`} title="Mover para baixo">↓</button>
                <button className="tc-button" type="button" onClick={() => editName(t, true)}>Nome no edital</button>
                <button className="tc-button tc-button-remove" type="button" onClick={() => act(() => axios.delete(`${apiUrl}adm/courseSubjects/${csId}/topics/${t.id}`), 'Tópico retirado do edital. O histórico foi preservado.')}>Retirar do edital</button>
              </div>
            </>}
          </li>)}
        </ul> : <p className="tc-empty">Esta matéria ainda não tem tópicos. Crie um abaixo ou selecione conteúdos do catálogo.</p>}
      </div>}

      <div className="tc-create-panel">
        <h4>Criar novo tópico</h4>
        <p className="tc-hint">{csId ? 'A matéria atual já está selecionada. Você pode escolher outra origem.' : 'Escolha a matéria à qual o conteúdo pertence e dê um nome ao tópico.'}</p>
        <form className="tc-create-form" onSubmit={createTopic}>
          <label>Matéria do novo tópico
            <select aria-label="Matéria do novo tópico" value={createSource} onChange={e => setCreateSource(e.target.value)}>
              <option value="">Selecione uma matéria</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label>Nome do tópico
            <input aria-label="Nome do novo tópico" placeholder="Ex.: Direitos e garantias fundamentais" value={name} onChange={e => setName(e.target.value)} maxLength={255} />
          </label>
          <button className="tc-button tc-button-primary" type="submit" disabled={!name.trim()}>{csId ? 'Criar e adicionar ao edital' : 'Criar tópico no catálogo'}</button>
        </form>
      </div>

      <div className="tc-section">
        <div className="tc-section-heading"><h4>{csId ? 'Reaproveitar tópicos do catálogo' : 'Tópicos cadastrados'}</h4><span className="tc-count">{filtered.length} disponíveis</span></div>
        {csId && <p className="tc-hint">Marque os tópicos que deseja adicionar. Você pode selecionar conteúdos de várias matérias.</p>}
        <div className="tc-filters">
          <label>Filtrar por matéria
            <select aria-label="Filtrar por matéria" value={source} onChange={e => setSource(e.target.value)}>
              <option value="">Todas as matérias</option>
              {rootSubjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label>Buscar tópico<input aria-label="Buscar tópico" placeholder="Digite o nome do tópico" value={search} onChange={e => setSearch(e.target.value)} /></label>
        </div>
        {!csId && <label className="tc-checkbox-option"><input type="checkbox" checked={archived} onChange={e => setArchived(e.target.checked)} /> Mostrar arquivados</label>}
        {loading ? <p className="tc-empty" role="status">Carregando tópicos…</p> : filtered.length ? <ul className="topic-catalog-list">
          {filtered.map(t => <li key={t.id} className={csId && selected.includes(t.id) ? 'tc-selected' : ''}>
            {csId ? <label className="tc-topic-option">
              <input type="checkbox" checked={selected.includes(t.id)} onChange={() => setSelected(ids => ids.includes(t.id) ? ids.filter(id => id !== t.id) : [...ids, t.id])} />
              <span className="tc-topic-info"><strong>{t.name}</strong><small>{t.subject_name} · {t.questions_count} questões</small></span>
            </label> : editing && !editing.local && editing.id === t.id ? renderEditor(t) : <>
              <span className="tc-topic-info"><strong>{t.name}</strong><small>{t.subject_name} · {t.questions_count} questões{!t.is_active ? ' · Arquivado' : ''}</small></span>
              <div className="topic-catalog-actions">
                <button className="tc-button" type="button" onClick={() => editName(t, false)}>Renomear</button>
                <button className="tc-button" type="button" onClick={() => act(() => axios.put(`${apiUrl}adm/catalogTopics/${t.id}`, { is_active: !t.is_active }))}>{t.is_active ? 'Arquivar' : 'Reativar'}</button>
                <button className="tc-button" type="button" onClick={() => act(async () => {
                  const { data } = await axios.get(`${apiUrl}adm/catalogTopics/${t.id}/usages`); setUsages({ name: t.name, rows: data.usages });
                })}>Ver usos</button>
              </div>
            </>}
          </li>)}
        </ul> : <p className="tc-empty">Nenhum tópico disponível para estes filtros. Você pode criar um novo acima.</p>}
        {csId && <div className="tc-selection-footer">
          <span>{selected.length ? `${selected.length} ${selected.length === 1 ? 'tópico selecionado' : 'tópicos selecionados'}. A seleção é mantida ao trocar os filtros.` : 'Marque pelo menos um tópico da lista para adicionar.'}</span>
          <button className="tc-button tc-button-primary" type="button" disabled={selected.length === 0} onClick={() => act(async () => {
            await axios.post(`${apiUrl}adm/courseSubjects/${csId}/topics`, { catalog_topic_ids: selected }); setSelected([]);
          }, 'Tópicos adicionados à matéria do concurso.')}>Adicionar selecionados ({selected.length})</button>
        </div>}
      </div>
    </fieldset>
    {busy && <p className="tc-hint" role="status">Salvando…</p>}
    {usages && <div className="tc-usages"><h4>Usos de {usages.name}</h4>
      {usages.rows.length === 0 ? <p>Ainda não utilizado em editais.</p> : <ul>{usages.rows.map(u => <li key={u.id}>{u.course_name} → {u.subject_name} → {u.name}{!u.is_active ? ' (retirado)' : ''}</li>)}</ul>}
    </div>}
  </section>;
}
